import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { Field, reduxForm } from 'redux-form';

import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Collapse from '@material-ui/core/Collapse';
import Typography from '@material-ui/core/Typography';

import Button from 'components/common/Button';
import IconButton from 'components/common/IconButton';
import IconFilter from 'components/icons/IconFilter';
import TextField from 'components/fields/TextField';

const styles = theme => ({
  root: {
    position: 'sticky',
    marginBottom: 16,
    top: 80,
    zIndex: 2,
    [theme.breakpoints.down('sm')]: {
      marginBottom: 8,
    },
  },
  rootBeforeTable: {
    top: 126,
    zIndex: 21,
    [theme.breakpoints.down('sm')]: {
      top: 72,
      marginBottom: 8,
      '&:before': {
        bottom: -8,
      },
    },
  },
  paper: {
    position: 'relative',
    zIndex: 2,
    padding: '14px 16px',
    [theme.breakpoints.down('sm')]: {
      padding: '8px 8px 0 8px',
    },
  },
  inputRoot: {
    padding: '2px 15px',
    boxSizing: 'border-box',
    width: '100%',
    borderRadius: 10,
    display: 'flex',
  },
  buttons: {
    display: 'flex',
    alignItems: 'center',
  },
  button: {
    '& + $button': {
      marginLeft: 16,
      [theme.breakpoints.down('sm')]: {
        marginLeft: 8,
      },
    },
  },
  gridContainer: {
    flexWrap: 'nowrap',
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
      flexDirection: 'column-reverse',
      alignItems: 'flex-start',
    },
  },
  gridItemForm: {
    flexShrink: 1,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      maxWidth: '100%',
      flexBasis: '100%',
    },
  },
  gridItemActions: {
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: 8,
    },
  },
  mobileButtonCounterContainer: {
    display: 'none',
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      alignItems: 'center',
    },
  },
  buttonFilter: {
    display: 'none',
    marginLeft: -10,
    marginBottom: -14,
    marginTop: -6,
    [theme.breakpoints.down('sm')]: {
      display: 'block',
    },
  },
  buttonFilterWithCounter: {
    marginLeft: -10,
    marginBottom: -6,
    marginTop: -6,
  },
  buttonFilterActive: {
    color: theme.palette.primary[500],
  },
  iconFilter: {
    width: 16,
    height: 16,
  },
  collapseContainer: {
    marginLeft: -8,
    marginRight: -8,
    paddingLeft: 8,
    paddingRight: 8,
  },
  collapseEntered: {
    overflow: 'visible',
  },
  form: {
    [theme.breakpoints.down('sm')]: {
      marginBottom: 8,
    },
  },
});

const Filter = ({
  className,
  handleSubmit,
  primaryButtonText,
  primaryButtonHref,
  primaryButtonDisabled,
  primaryButtonLoading,
  onPrimaryButtonClick,
  secondaryButtonText,
  secondaryButtonHref,
  secondaryButtonDisabled,
  secondaryButtonLoading,
  onSecondaryButtonClick,
  placeholder,
  searchWidth,
  secondaryFilter,
  top,
  beforeTable,
  onMobileFilterBottonClick,
  mobileFilterExpanded,
  filters,
  showTableCount,
  count,
  loading,
  items,
  theme,
  showPrimaryFilter,
  classes,
}) => {
  const expanded = mobileFilterExpanded !== undefined ? mobileFilterExpanded : true;

  return (
    <div
      className={classNames(classes.root, className, {
        [classes.rootBeforeTable]: beforeTable,
      })}
      {...top ? {
        style: { top: `${top}px` },
      } : {}}
    >
      <Paper className={classes.paper}>
        <Grid
          className={classes.gridContainer}
          container
          justify="space-between"
          alignItems="center"
          spacing={0}
        >
          <Grid
            className={classes.gridItemForm}
            item
            xs={12}
          >
            <Collapse
              in={expanded}
              classes={{
                container: classes.collapseContainer,
                entered: classes.collapseEntered,
              }}
              timeout={theme.transitions.duration.short}
            >
              <form
                className={classes.form}
                noValidate
                autoComplete="off"
                onSubmit={handleSubmit}
              >
                <Grid
                  container
                  alignItems="center"
                  spacing={24}
                >
                  {showPrimaryFilter && (
                    <Grid
                      item
                      xs={12}
                      md={searchWidth}
                    >
                      <Field
                        id="Filter"
                        name="Filter"
                        component={TextField}
                        placeholder={placeholder}
                        fullWidth
                        endAdornmentClearButton
                      />
                    </Grid>
                  )}
                  {secondaryFilter}
                </Grid>
              </form>
            </Collapse>
          </Grid>
          {(onMobileFilterBottonClick || secondaryButtonText || primaryButtonText) &&
            <Grid className={classes.gridItemActions} item>
              {onMobileFilterBottonClick && showTableCount &&
                <div className={classes.mobileButtonCounterContainer}>
                  <IconButton
                    onClick={onMobileFilterBottonClick}
                    className={classNames(classes.buttonFilterWithCounter, {
                      [classes.buttonFilterActive]: filters && Object.keys(filters).length > 0,
                    })}
                  >
                    <IconFilter className={classes.iconFilter} />
                  </IconButton>
                  {(loading === 0 || items.length > 0) && (
                    <Typography>
                      {`(${count})`}
                    </Typography>
                  )}
                </div>
              }
              {onMobileFilterBottonClick && !showTableCount &&
                <IconButton
                  onClick={onMobileFilterBottonClick}
                  className={classNames(classes.buttonFilter, {
                    [classes.buttonFilterActive]: filters && Object.keys(filters).length > 0,
                  })}
                >
                  <IconFilter className={classes.iconFilter} />
                </IconButton>
              }
              <div className={classes.buttons}>
                {secondaryButtonText &&
                  <Button
                    className={classes.button}
                    variant="raised"
                    disabled={secondaryButtonDisabled}
                    loading={secondaryButtonLoading}
                    {...secondaryButtonHref ? { href: secondaryButtonHref } : {}}
                    {...onSecondaryButtonClick ? { onClick: onSecondaryButtonClick } : {}}
                  >
                    {secondaryButtonText}
                  </Button>
                }
                {primaryButtonText &&
                  <Button
                    className={classes.button}
                    variant="raised"
                    color="primary"
                    disabled={primaryButtonDisabled}
                    loading={primaryButtonLoading}
                    {...primaryButtonHref ? { href: primaryButtonHref } : {}}
                    {...onPrimaryButtonClick ? { onClick: onPrimaryButtonClick } : {}}
                  >
                    {primaryButtonText}
                  </Button>
                }
              </div>
            </Grid>
          }
        </Grid>
      </Paper>
    </div>
  );
};

Filter.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  form: PropTypes.string.isRequired,
  primaryButtonText: PropTypes.string,
  primaryButtonHref: PropTypes.string,
  primaryButtonDisabled: PropTypes.bool,
  primaryButtonLoading: PropTypes.bool,
  onPrimaryButtonClick: PropTypes.func,
  secondaryButtonText: PropTypes.string,
  secondaryButtonHref: PropTypes.string,
  secondaryButtonDisabled: PropTypes.bool,
  secondaryButtonLoading: PropTypes.bool,
  onSecondaryButtonClick: PropTypes.func,
  placeholder: PropTypes.string,
  searchWidth: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
  secondaryFilter: PropTypes.node,
  beforeTable: PropTypes.bool,
  className: PropTypes.string,
  top: PropTypes.number,
  onMobileFilterBottonClick: PropTypes.func,
  mobileFilterExpanded: PropTypes.bool,
  filters: PropTypes.object,
  showTableCount: PropTypes.bool,
  count: PropTypes.number,
  loading: PropTypes.number,
  items: PropTypes.arrayOf(PropTypes.object),
  theme: PropTypes.object.isRequired,
  showPrimaryFilter: PropTypes.bool,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    inputRoot: PropTypes.string.isRequired,
    button: PropTypes.string.isRequired,
  }).isRequired,
};

Filter.defaultProps = {
  placeholder: '',
  primaryButtonText: null,
  primaryButtonHref: null,
  onPrimaryButtonClick: null,
  secondaryButtonText: null,
  secondaryButtonHref: null,
  onSecondaryButtonClick: null,
  searchWidth: 5,
  secondaryFilter: null,
  primaryButtonDisabled: false,
  secondaryButtonDisabled: false,
  primaryButtonLoading: false,
  secondaryButtonLoading: false,
  beforeTable: false,
  className: '',
  top: undefined,
  onMobileFilterBottonClick: null,
  mobileFilterExpanded: undefined,
  filters: null,
  showTableCount: false,
  count: 0,
  loading: 0,
  items: [],
  showPrimaryFilter: true,
};

export default reduxForm({
  onSubmit: () => {},
  destroyOnUnmount: false,
})(withStyles(styles, { withTheme: true })(Filter));



// WEBPACK FOOTER //
// ./src/components/common/Filter/Filter.js